'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AYAT_BRIDGE } from '../app/muslim-resource-guide/components/irlData';
import { FISSION_EXTENDED_NAMES, FISSION_SEQ } from '../app/muslim-resource-guide/components/fissionData';
import { ECO_TILES } from '../app/muslim-resource-guide/components/ecoData';
import Link from 'next/link';

// ─── DATA ─────────────────────────────────────────────────────────────────────

const BASE_NAMES = Object.entries(AYAT_BRIDGE).map(([key, val]) => ({
  id: key.toLowerCase().replace('al-', '').replace('ar-', '').replace('ash-', '').replace('as-', ''),
  ar: (val as { ar: string; en: string; cat?: string; ayat?: string[]; procs?: string[]; field?: string; sim?: string }).ar,
  en: key,
  m: (val as { ar: string; en: string }).en,
  cat: (val as { cat?: string }).cat || 'Community',
  ayat: (val as { ayat?: string[] }).ayat || [],
  procs: (val as { procs?: string[] }).procs || [],
  field: (val as { field?: string }).field || '',
  sim: (val as { sim?: string }).sim || '',
}));

const ALL_NAMES = [
  ...BASE_NAMES,
  ...FISSION_EXTENDED_NAMES.map(n => ({
    id: n.id, ar: n.ar, en: n.en, m: n.m, cat: n.cat,
    ayat: n.ayat, procs: n.procs, field: n.field, sim: n.sim,
  })),
];

const COMM_TILES_SEARCH = [
  { id: 'd1', title: 'Brightmoor Prairie', city: 'Detroit', cat: 'Health', names: ['muhyi', 'khaliq'], desc: 'Vacant lots in Brightmoor returning to tallgrass prairie.' },
  { id: 'd2', title: 'Banglatown Rizq', city: 'Detroit', cat: 'Food', names: ['razzaq', 'jami'], desc: 'Banglatown halal markets on Conant Street.' },
  { id: 'c1', title: 'Roseland Transit Desert', city: 'Chicago', cat: 'Income', names: ['adl', 'sabur'], desc: 'Roseland has waited 60+ years for the Red Line extension.' },
  { id: 'a1', title: 'West End Walking', city: 'Atlanta', cat: 'Community', names: ['basir', 'jami'], desc: 'West End walking circles combining spiritual practice with neighborhood observation.' },
  { id: 'p1', title: 'Hill District Masjid', city: 'Pittsburgh', cat: 'Housing', names: ['baqi', 'sabur'], desc: 'Masajid in the Hill District persisting through urban renewal.' },
  { id: 'l1', title: '9th Street Divide', city: 'Louisville', cat: 'Income', names: ['adl', 'haqq'], desc: "Louisville\'s 9th Street — a physical wall of segregation infrastructure." },
  { id: 'po1', title: 'Alberta Displacement', city: 'Portland', cat: 'Community', names: ['haqq', 'shahid'], desc: 'Alberta Arts District — once the heart of Black Portland.' },
  { id: 'ci1', title: 'Peebles Corner Void', city: 'Cincinnati', cat: 'Community', names: ['shahid', 'haqq'], desc: 'Peebles Corner — where institutions withdrew.' },
  { id: 'o1', title: '24th and Lake', city: 'Omaha', cat: 'Community', names: ['basir', 'shahid'], desc: '24th and Lake — once the hub of Black Omaha.' },
];

const KEY_CONNECTIONS: Record<string, string[]> = {
  'wadud': ['Key 03 TAQWAH', 'Key 04 MIZAN', 'Key 05 AMANAH'],
  'hakeem': ['Key 01 AYAT', 'Key 03 TAQWAH', 'Key 04 MIZAN'],
  'razzaq': ['Key 02 HAAL', 'Key 04 MIZAN', 'Key 05 AMANAH'],
  'hadi': ['Key 01 AYAT', 'Key 03 TAQWAH', 'Key 05 AMANAH'],
  'latif': ['Key 01 AYAT', 'Key 02 HAAL', 'Key 03 TAQWAH'],
  'jami': ['Key 02 HAAL', 'Key 04 MIZAN'],
  'adl': ['Key 04 MIZAN', 'Key 05 AMANAH'],
  'sabur': ['Key 01 AYAT', 'Key 03 TAQWAH'],
  'khaliq': ['Key 01 AYAT', 'Key 04 MIZAN'],
  'basir': ['Key 01 AYAT', 'Key 02 HAAL'],
  'haqq': ['Key 01 AYAT', 'Key 05 AMANAH'],
  'baqi': ['Key 03 TAQWAH', 'Key 05 AMANAH'],
};

const CSS = `
  .nso-overlay { position: fixed; inset: 0; background: rgba(6,13,18,0.97); backdrop-filter: blur(20px); z-index: 300; display: flex; flex-direction: column; opacity: 0; pointer-events: none; transition: opacity 0.3s; }
  .nso-overlay.open { opacity: 1; pointer-events: all; }
  .nso-top { padding: 24px 24px 16px; border-bottom: 1px solid rgba(201,168,76,0.08); }
  .nso-search-wrap { position: relative; max-width: 600px; margin: 0 auto; }
  .nso-search { width: 100%; padding: 12px 16px; background: rgba(10,25,41,0.5); border: 1px solid rgba(201,168,76,0.2); color: #f0ece4; font-size: 16px; border-radius: 4px; outline: none; font-family: 'Georgia', serif; transition: border-color 0.2s; }
  .nso-search:focus { border-color: rgba(201,168,76,0.5); }
  .nso-search::placeholder { color: rgba(201,168,76,0.3); font-style: italic; }
  .nso-close { position: absolute; top: 24px; right: 24px; background: none; border: 1px solid rgba(61,54,39,0.4); color: #7a6f5d; cursor: pointer; padding: 6px 12px; font-size: 11px; border-radius: 3px; font-family: inherit; transition: all 0.2s; }
  .nso-close:hover { color: #d4c5a9; border-color: rgba(201,168,76,0.3); }
  .nso-filters { display: flex; gap: 6px; flex-wrap: wrap; max-width: 600px; margin: 12px auto 0; }
  .nso-filter { padding: 3px 10px; border: 1px solid rgba(61,54,39,0.3); background: transparent; color: #7a6f5d; font-size: 9px; cursor: pointer; border-radius: 20px; font-family: inherit; transition: all 0.2s; letter-spacing: .06em; }
  .nso-filter:hover { color: #d4c5a9; }
  .nso-filter.active { color: #c9a84c; border-color: rgba(201,168,76,0.4); background: rgba(201,168,76,0.06); }
  .nso-body { flex: 1; overflow-y: auto; padding: 20px 24px; }
  .nso-body::-webkit-scrollbar { width: 4px; }
  .nso-body::-webkit-scrollbar-track { background: transparent; }
  .nso-body::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.2); border-radius: 2px; }
  .nso-section-lbl { font-size: 8px; color: rgba(201,168,76,0.4); letter-spacing: .15em; text-transform: uppercase; margin-bottom: 12px; }
  .nso-results { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 12px; max-width: 1200px; margin: 0 auto; }
  .nso-card { background: rgba(10,25,41,0.4); border: 1px solid rgba(61,54,39,0.3); border-radius: 6px; overflow: hidden; transition: border-color 0.2s; }
  .nso-card:hover { border-color: rgba(201,168,76,0.25); }
  .nso-card-head { padding: 10px 14px; background: rgba(6,13,18,0.4); border-bottom: 1px solid rgba(61,54,39,0.2); display: flex; align-items: center; gap: 10px; }
  .nso-card-ar { font-size: 22px; color: #c9a84c; }
  .nso-card-en { font-size: 13px; color: #f0ece4; font-weight: bold; }
  .nso-card-m { font-size: 10px; color: #7a6f5d; }
  .nso-card-cat { margin-left: auto; font-size: 8px; color: #2d8a87; border: 1px solid rgba(45,138,135,0.2); padding: 1px 6px; border-radius: 2px; }
  .nso-card-body { padding: 10px 14px; }
  .nso-card-ayah { font-size: 11px; color: #d4c5a9; line-height: 1.5; font-style: italic; padding: 6px 8px; background: rgba(6,13,18,0.3); border-left: 2px solid #c9a84c; border-radius: 0 3px 3px 0; margin-bottom: 8px; }
  .nso-card-section { margin-bottom: 8px; }
  .nso-card-section-lbl { font-size: 8px; color: #2d8a87; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 4px; }
  .nso-seq-pill { display: inline-block; padding: 2px 7px; margin: 2px; background: rgba(45,122,71,0.08); border: 1px solid rgba(45,122,71,0.2); border-radius: 3px; font-size: 9px; color: #2d7a47; cursor: pointer; transition: all 0.2s; }
  .nso-seq-pill:hover { background: rgba(45,122,71,0.15); }
  .nso-comm-pill { display: inline-block; padding: 2px 7px; margin: 2px; background: rgba(45,138,135,0.08); border: 1px solid rgba(45,138,135,0.2); border-radius: 3px; font-size: 9px; color: #2d8a87; cursor: pointer; transition: all 0.2s; }
  .nso-comm-pill:hover { background: rgba(45,138,135,0.15); }
  .nso-key-pill { display: inline-block; padding: 2px 7px; margin: 2px; background: rgba(201,168,76,0.06); border: 1px solid rgba(201,168,76,0.2); border-radius: 3px; font-size: 9px; color: #c9a84c; }
  .nso-card-actions { display: flex; gap: 6px; margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(61,54,39,0.15); }
  .nso-action-btn { padding: 4px 10px; border: 1px solid rgba(201,168,76,0.2); background: transparent; color: #c9a84c; font-size: 9px; cursor: pointer; border-radius: 3px; font-family: inherit; transition: all 0.2s; text-decoration: none; display: inline-block; }
  .nso-action-btn:hover { background: rgba(201,168,76,0.06); }
  .nso-action-btn-primary { padding: 4px 10px; border: 1px solid rgba(201,168,76,0.5); background: rgba(201,168,76,0.1); color: #e0c878; font-size: 9px; cursor: pointer; border-radius: 3px; font-family: inherit; transition: all 0.2s; }
  .nso-action-btn-primary:hover { background: rgba(201,168,76,0.18); }
  .nso-freq-section { max-width: 800px; margin: 0 auto; }
  .nso-freq-tier { margin-bottom: 20px; }
  .nso-freq-tier-lbl { font-size: 8px; color: #7a6f5d; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 8px; }
  .nso-freq-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .nso-freq-pill-high { padding: 6px 14px; background: rgba(201,168,76,0.1); border: 1px solid rgba(201,168,76,0.3); border-radius: 20px; font-size: 13px; color: #c9a84c; cursor: pointer; transition: all 0.2s; }
  .nso-freq-pill-high:hover { background: rgba(201,168,76,0.18); }
  .nso-freq-pill-med { padding: 4px 10px; background: rgba(45,138,135,0.08); border: 1px solid rgba(45,138,135,0.25); border-radius: 20px; font-size: 11px; color: #2d8a87; cursor: pointer; transition: all 0.2s; }
  .nso-freq-pill-med:hover { background: rgba(45,138,135,0.15); }
  .nso-freq-pill-low { padding: 3px 8px; background: rgba(61,54,39,0.15); border: 1px solid rgba(61,54,39,0.25); border-radius: 20px; font-size: 9px; color: #7a6f5d; cursor: pointer; transition: all 0.2s; }
  .nso-freq-pill-low:hover { background: rgba(61,54,39,0.25); }
  .nso-empty { text-align: center; padding: 48px 24px; color: #7a6f5d; font-size: 13px; font-style: italic; }
`;

interface NamesSearchOverlayProps {
  open: boolean;
  onClose: () => void;
  onNameSelect?: (nameId: string, nameAr: string, nameEn: string, cityId?: string, tileId?: string) => void;
}

export default function NamesSearchOverlay({ open, onClose, onNameSelect }: NamesSearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [filterMode, setFilterMode] = useState<'ALL NAMES' | 'BY KEY' | 'BY SEQUENCE' | 'BY FREQUENCY'>('ALL NAMES');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (open) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const getNameFreq = useCallback((nid: string) => {
    const ecoCount = ECO_TILES.filter(t => t.names.includes(nid)).length;
    const fissionCount = FISSION_SEQ.filter(t => t.names.includes(nid)).length;
    const commCount = COMM_TILES_SEARCH.filter(t => t.names.includes(nid)).length;
    return ecoCount + fissionCount + commCount;
  }, []);

  const getNameSequences = useCallback((nid: string) => {
    return FISSION_SEQ.filter(t => t.names.includes(nid)).map(t => ({ id: t.id, title: t.title, loc: t.loc }));
  }, []);

  const getNameCommTiles = useCallback((nid: string) => {
    return COMM_TILES_SEARCH.filter(t => t.names.includes(nid));
  }, []);

  const searchResults = query.trim().length > 0
    ? ALL_NAMES.filter(n =>
        n.en.toLowerCase().includes(query.toLowerCase()) ||
        n.ar.includes(query) ||
        n.m.toLowerCase().includes(query.toLowerCase()) ||
        n.sim.toLowerCase().includes(query.toLowerCase())
      )
    : filterMode === 'BY FREQUENCY'
    ? ALL_NAMES
    : filterMode === 'BY SEQUENCE'
    ? ALL_NAMES.filter(n => getNameSequences(n.id).length > 0)
    : filterMode === 'BY KEY'
    ? ALL_NAMES.filter(n => KEY_CONNECTIONS[n.id])
    : ALL_NAMES.slice(0, 20);

  const highFreq = ALL_NAMES.filter(n => getNameFreq(n.id) >= 5);
  const medFreq = ALL_NAMES.filter(n => { const f = getNameFreq(n.id); return f >= 3 && f < 5; });
  const lowFreq = ALL_NAMES.filter(n => { const f = getNameFreq(n.id); return f >= 1 && f < 3; });

  const showFreqView = !query.trim() && filterMode === 'BY FREQUENCY';

  function handleOpenInIRLLens(n: typeof ALL_NAMES[0], cityId?: string, tileId?: string) {
    if (onNameSelect) {
      onNameSelect(n.id, n.ar, n.en, cityId, tileId);
    }
    onClose();
    // Smooth scroll to the unified IRL lens component
    setTimeout(() => {
      const el = document.getElementById('unified-irl-lens');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 200);
  }

  return (
    <>
      <style>{CSS}</style>
      <div className={`nso-overlay${open ? ' open' : ''}`} role="dialog" aria-modal="true">
        <div className="nso-top">
          <div className="nso-search-wrap">
            <input
              ref={inputRef}
              className="nso-search"
              placeholder="Search Names, sequences, Keys..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              aria-label="Search Names and sequences"
            />
          </div>
          <div className="nso-filters">
            {(['ALL NAMES', 'BY KEY', 'BY SEQUENCE', 'BY FREQUENCY'] as const).map(f => (
              <button key={f} className={`nso-filter${filterMode === f ? ' active' : ''}`} onClick={() => setFilterMode(f)}>{f}</button>
            ))}
          </div>
          <button className="nso-close" onClick={onClose}>Close ✕</button>
        </div>

        <div className="nso-body">
          {showFreqView ? (
            <div className="nso-freq-section">
              <div className="nso-section-lbl">Names by Frequency of Appearance</div>
              <div className="nso-freq-tier">
                <div className="nso-freq-tier-lbl">HIGH — appears in 5+ sequences</div>
                <div className="nso-freq-pills">
                  {highFreq.map(n => (
                    <button key={n.id} className="nso-freq-pill-high" onClick={() => { setQuery(n.en); setFilterMode('ALL NAMES'); }}>{n.ar}</button>
                  ))}
                </div>
              </div>
              <div className="nso-freq-tier">
                <div className="nso-freq-tier-lbl">MEDIUM — 3–4 sequences</div>
                <div className="nso-freq-pills">
                  {medFreq.map(n => (
                    <button key={n.id} className="nso-freq-pill-med" onClick={() => { setQuery(n.en); setFilterMode('ALL NAMES'); }}>{n.ar}</button>
                  ))}
                </div>
              </div>
              <div className="nso-freq-tier">
                <div className="nso-freq-tier-lbl">LOW — 1–2 sequences</div>
                <div className="nso-freq-pills">
                  {lowFreq.map(n => (
                    <button key={n.id} className="nso-freq-pill-low" onClick={() => { setQuery(n.en); setFilterMode('ALL NAMES'); }}>{n.ar}</button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {searchResults.length === 0 ? (
                <div className="nso-empty">No Names found for &quot;{query}&quot;</div>
              ) : (
                <>
                  <div className="nso-section-lbl">
                    {query.trim() ? `${searchResults.length} results for "${query}"` : `${searchResults.length} Names`}
                  </div>
                  <div className="nso-results">
                    {searchResults.slice(0, 24).map(n => {
                      const seqs = getNameSequences(n.id);
                      const comms = getNameCommTiles(n.id);
                      const keys = KEY_CONNECTIONS[n.id] || [];
                      const firstAyah = n.ayat[0] || '';
                      const firstCommTile = comms[0];
                      return (
                        <div key={n.id} className="nso-card">
                          <div className="nso-card-head">
                            <div className="nso-card-ar">{n.ar}</div>
                            <div>
                              <div className="nso-card-en">{n.en} — {n.m}</div>
                            </div>
                            <div className="nso-card-cat">{n.cat}</div>
                          </div>
                          <div className="nso-card-body">
                            {firstAyah && (
                              <div className="nso-card-ayah">{firstAyah}</div>
                            )}
                            {seqs.length > 0 && (
                              <div className="nso-card-section">
                                <div className="nso-card-section-lbl">APPEARS IN SEQUENCES ({seqs.length})</div>
                                {seqs.slice(0, 4).map(s => (
                                  <span key={s.id} className="nso-seq-pill">{s.id.toUpperCase()}: {s.title}</span>
                                ))}
                                {seqs.length > 4 && <span className="nso-seq-pill">+{seqs.length - 4} more</span>}
                              </div>
                            )}
                            {comms.length > 0 && (
                              <div className="nso-card-section">
                                <div className="nso-card-section-lbl">COMMUNITY TILES ({comms.length})</div>
                                {comms.slice(0, 3).map(t => (
                                  <span key={t.id} className="nso-comm-pill" onClick={() => handleOpenInIRLLens(n, t.city, t.id)}>{t.city}: {t.title}</span>
                                ))}
                                {comms.length > 3 && <span className="nso-comm-pill">+{comms.length - 3} more</span>}
                              </div>
                            )}
                            {keys.length > 0 && (
                              <div className="nso-card-section">
                                <div className="nso-card-section-lbl">CROSS-KEY CONNECTIONS</div>
                                {keys.map((k, i) => <span key={i} className="nso-key-pill">{k}</span>)}
                              </div>
                            )}
                            <div className="nso-card-actions">
                              <button
                                className="nso-action-btn-primary"
                                onClick={() => handleOpenInIRLLens(n, firstCommTile?.city, firstCommTile?.id)}
                              >
                                ✦ Open in IRL Lens
                              </button>
                              <Link href="/keys-to-kulli-haal" className="nso-action-btn" onClick={onClose}>→ Keys Curriculum</Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
